import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/component/navbar';
import { showAlert } from '@/components/AppDialog';

type EmirateOption = 'sharjah' | 'other';

const otherEmirates = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ras Al Khaimah'];

// Phase 1: Mainland incorporation is supported for Dubai only. Free Zone keeps the full list.
const MAINLAND_ONLY_MSG = 'At the moment we only support Dubai Mainland. Support for other Emirates will be available soon.';

const RadioCircle = ({ selected }: { selected: boolean }) => (
  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${selected ? 'border-amber-500' : 'border-gray-400'}`}>
    {selected && <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />}
  </div>
);

const EMIRATE_CSS = `
.sem-stage{position:relative;width:100%;min-height:540px;display:flex;align-items:center;justify-content:center;overflow:hidden}
.sem-stage .glow{position:absolute;border-radius:50%;filter:blur(72px);opacity:.5}
.sem-stage .glow.g1{width:360px;height:360px;background:#FBBF24;top:4%;left:-14%;animation:sem-glow 7s ease-in-out infinite}
.sem-stage .glow.g2{width:300px;height:300px;background:#FDE68A;bottom:-4%;right:-12%;animation:sem-glow 9s ease-in-out infinite reverse}
.sem-stage .grid{position:absolute;inset:0;background-image:radial-gradient(rgba(176,125,0,.16) 1px,transparent 1px);background-size:22px 22px;opacity:.6;animation:sem-drift 28s linear infinite}
.sem-card{position:relative;width:340px;background:#fff;border:1px solid #F0E6CC;border-radius:22px;box-shadow:0 32px 64px -26px rgba(120,85,0,.5);padding:20px;animation:sem-rise .7s cubic-bezier(.2,.8,.2,1) both,sem-float 6.5s ease-in-out 1s infinite}
.sem-head{display:flex;align-items:center;gap:8px;margin-bottom:14px}
.sem-eyebrow{font-size:10px;font-weight:800;letter-spacing:.14em;color:#B07D00;text-transform:uppercase}
.sem-uae{margin-left:auto;font-size:10px;font-weight:800;color:#8a6400;background:#FFF3D1;border:1px solid #F3DFA0;padding:3px 9px;border-radius:999px}
.sem-map{position:relative;height:236px;border-radius:16px;overflow:hidden;border:1px solid #F3E6BD;background:linear-gradient(180deg,#FFFBEF,#FFF3D1)}
.sem-map .mgrid{position:absolute;inset:0;background-image:radial-gradient(rgba(176,125,0,.14) 1px,transparent 1px);background-size:18px 18px;opacity:.55}
.sem-pinlabel{position:absolute;left:50%;top:34px;transform:translateX(-50%);display:flex;align-items:center;gap:6px;background:#17263A;color:#fff;font-size:13px;font-weight:800;padding:7px 13px;border-radius:999px;white-space:nowrap;box-shadow:0 10px 24px -10px rgba(23,38,58,.6);animation:sem-label-in .5s cubic-bezier(.2,.8,.2,1) 1.25s both;z-index:3}
.sem-pinlabel::after{content:'';position:absolute;left:50%;bottom:-5px;transform:translateX(-50%) rotate(45deg);width:10px;height:10px;background:#17263A;border-radius:2px}
.sem-pinlabel svg{width:13px;height:13px}
.sem-cap{display:flex;align-items:center;gap:7px;margin-top:13px;font-size:11.5px;font-weight:700;color:#6b7280}
.sem-cap svg{width:15px;height:15px;flex-shrink:0}
.sem-chip{position:absolute;display:flex;align-items:center;gap:7px;background:#fff;border:1px solid #F0E6CC;border-radius:12px;padding:8px 12px;box-shadow:0 14px 30px -14px rgba(120,85,0,.4);font-size:12px;font-weight:700;color:#17263A;white-space:nowrap}
.sem-chip svg{width:16px;height:16px}
.sem-chip.a{top:13%;left:5%;animation:sem-rise .6s cubic-bezier(.2,.8,.2,1) .5s both,sem-floatA 5s ease-in-out 1.2s infinite}
.sem-chip.b{bottom:15%;left:2%;animation:sem-rise .6s cubic-bezier(.2,.8,.2,1) .7s both,sem-floatB 5.6s ease-in-out 1.4s infinite}
.sem-chip.c{bottom:24%;right:4%;animation:sem-rise .6s cubic-bezier(.2,.8,.2,1) .6s both,sem-floatB 4.6s ease-in-out 1s infinite}
.sem-coin{width:22px;height:22px;border-radius:50%;background:linear-gradient(135deg,#FDE68A,#F59E0B);display:flex;align-items:center;justify-content:center;color:#fff}
.sem-coin svg{width:13px;height:13px}
.sem-pin{transform-origin:center;animation:sem-drop 1s cubic-bezier(.3,1.4,.5,1) .85s both}
.sem-ring{transform-origin:150px 196px;animation:sem-ring 2.4s ease-out 1.6s infinite}
.sem-ring.r2{animation-delay:2.4s}
.sem-tower{transform-origin:bottom;animation:sem-grow .7s cubic-bezier(.2,.9,.3,1) both}
@keyframes sem-glow{0%,100%{transform:translate(0,0) scale(1);opacity:.5}50%{transform:translate(-10px,-12px) scale(1.08);opacity:.65}}
@keyframes sem-drift{to{background-position:22px 22px}}
@keyframes sem-rise{from{opacity:0;transform:translateY(16px)}to{opacity:1}}
@keyframes sem-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-11px)}}
@keyframes sem-floatA{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
@keyframes sem-floatB{0%,100%{transform:translateY(0)}50%{transform:translateY(11px)}}
@keyframes sem-drop{0%{opacity:0;transform:translateY(-46px)}60%{opacity:1}100%{opacity:1;transform:translateY(0)}}
@keyframes sem-ring{0%{transform:scale(.3);opacity:.55}100%{transform:scale(1.5);opacity:0}}
@keyframes sem-grow{from{opacity:0;transform:scaleY(.4)}to{opacity:1;transform:scaleY(1)}}
@keyframes sem-label-in{from{opacity:0;transform:translateX(-50%) translateY(-6px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}
@media (prefers-reduced-motion: reduce){.sem-stage *{animation:none !important}}
`;

const EmirateIllustration = ({ emirate }: { emirate: string }) => (
  <div className="sem-stage">
    <style>{EMIRATE_CSS}</style>
    <div className="glow g1" /><div className="glow g2" /><div className="grid" />

    {/* Floating accents */}
    <div className="sem-chip a">
      <svg viewBox="0 0 24 24" fill="none" stroke="#15824E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
      Free Zone ready
    </div>
    <div className="sem-chip b">
      <span className="sem-coin"><svg viewBox="0 0 24 24"><path d="M5.5 4.2c0-.5.4-.9.9-.9h4.2c4.7 0 7.9 3.3 7.9 8.5s-3.2 8.7-7.9 8.7H6.4c-.5 0-.9-.4-.9-.9V4.2z" fill="none" stroke="currentColor" strokeWidth="2.1"/><line x1="2.5" y1="10.2" x2="16.5" y2="10.2" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round"/><line x1="2.5" y1="14.2" x2="16.5" y2="14.2" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round"/></svg></span>
      0 setup fee
    </div>
    <div className="sem-chip c">
      <svg viewBox="0 0 24 24" fill="#F59E0B"><path d="M12 2l2.2 6.6H21l-5.4 4 2 6.6L12 15.6 6.4 19.2l2-6.6L3 8.6h6.8z"/></svg>
      Fast incorporation
    </div>

    {/* Map card — pin labelled by the selected emirate */}
    <div className="sem-card">
      <div className="sem-head">
        <span className="sem-eyebrow">Formation location</span>
        <span className="sem-uae">🇦🇪 UAE</span>
      </div>

      <div className="sem-map">
        <div className="mgrid" />
        <div className="sem-pinlabel">
          <svg viewBox="0 0 24 24" fill="#FBBF24"><path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="#17263A"/></svg>
          {emirate}
        </div>

        <svg viewBox="0 0 300 236" width="100%" height="100%" fill="none" preserveAspectRatio="xMidYMax meet" style={{ position: 'absolute', inset: 0 }}>
          {/* water */}
          <path d="M205 236 C 215 200 250 196 300 200 L300 236 Z" fill="#BFE3F2" opacity="0.55" />
          <path d="M30 236 C 60 224 90 232 120 226 C 150 220 175 228 205 236 Z" fill="#FBE9B8" opacity="0.6" />

          {/* skyline */}
          <g>
            <rect className="sem-tower" style={{ animationDelay: '.55s' }} x="70" y="150" width="26" height="74" rx="3" fill="url(#sem-g)" />
            <rect className="sem-tower" style={{ animationDelay: '.62s' }} x="100" y="128" width="22" height="96" rx="3" fill="url(#sem-g)" />
            {/* tall needle tower (Burj-like) */}
            <polygon className="sem-tower" style={{ animationDelay: '.7s' }} points="150,58 158,120 156,224 144,224 142,120" fill="url(#sem-g)" />
            <line x1="150" y1="44" x2="150" y2="58" stroke="#E6920B" strokeWidth="2.4" strokeLinecap="round" />
            <rect className="sem-tower" style={{ animationDelay: '.62s' }} x="170" y="138" width="22" height="86" rx="3" fill="url(#sem-g)" />
            <rect className="sem-tower" style={{ animationDelay: '.55s' }} x="196" y="162" width="24" height="62" rx="3" fill="url(#sem-g)" />
            {/* windows */}
            <g fill="#fff" opacity="0.7">
              <rect x="76" y="158" width="4" height="4" rx="1" /><rect x="86" y="158" width="4" height="4" rx="1" />
              <rect x="76" y="170" width="4" height="4" rx="1" /><rect x="86" y="170" width="4" height="4" rx="1" />
              <rect x="106" y="138" width="4" height="4" rx="1" /><rect x="114" y="138" width="4" height="4" rx="1" />
              <rect x="176" y="148" width="4" height="4" rx="1" /><rect x="184" y="148" width="4" height="4" rx="1" />
              <rect x="202" y="172" width="4" height="4" rx="1" /><rect x="210" y="172" width="4" height="4" rx="1" />
            </g>
            {/* ground line */}
            <line x1="20" y1="224" x2="280" y2="224" stroke="#EAD79A" strokeWidth="2" />
          </g>

          {/* ripple rings at pin base */}
          <circle className="sem-ring" cx="150" cy="196" r="16" fill="none" stroke="#F59E0B" strokeWidth="2.4" />
          <circle className="sem-ring r2" cx="150" cy="196" r="16" fill="none" stroke="#F59E0B" strokeWidth="2.4" />

          {/* location pin */}
          <g className="sem-pin">
            <ellipse cx="150" cy="197" rx="9" ry="3" fill="rgba(120,85,0,.25)" />
            <path d="M150 154 c-9 0 -16 7 -16 16 c0 11 16 26 16 26 s16 -15 16 -26 c0 -9 -7 -16 -16 -16 z" fill="url(#sem-pin-g)" />
            <circle cx="150" cy="170" r="6" fill="#fff" />
          </g>

          <defs>
            <linearGradient id="sem-g" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#FCD34D" /><stop offset="1" stopColor="#F59E0B" />
            </linearGradient>
            <linearGradient id="sem-pin-g" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#FBBF24" /><stop offset="1" stopColor="#E6920B" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="sem-cap">
        <svg viewBox="0 0 24 24" fill="none" stroke="#15824E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
        Licensed &amp; registered in the UAE
      </div>
    </div>
  </div>
);

export default function SetupEmirate() {
  const isMainland = (localStorage.getItem('setupBusinessType') || '').toLowerCase() === 'mainland';
  const primaryEmirate = isMainland ? 'Dubai' : 'Sharjah';
  const [selected, setSelected] = useState<EmirateOption>('sharjah');
  const [otherValue, setOtherValue] = useState('Dubai');
  const navigate = useNavigate();

  const displayEmirate = selected === 'sharjah' ? primaryEmirate : otherValue;

  // On the mainland path, only Dubai is supported in Phase 1 — surface the popup
  // the moment a client picks any other Emirate.
  const handleOther = (val: string) => {
    setOtherValue(val);
    if (isMainland && val !== 'Dubai') showAlert(MAINLAND_ONLY_MSG);
  };

  const handleNext = () => {
    if (isMainland && displayEmirate !== 'Dubai') { showAlert(MAINLAND_ONLY_MSG); return; }
    localStorage.setItem('setupEmirate', displayEmirate);
    navigate('/setup-plan');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar hideSignBtn={false} />
      <div className="flex flex-1">
        {/* Left panel */}
        <div
          className="hidden md:flex w-1/2 items-stretch justify-center"
          style={{ backgroundColor: '#FFFBEB', minHeight: 'calc(100vh - 64px)' }}
        >
          <EmirateIllustration emirate={displayEmirate} />
        </div>

        {/* Right panel */}
        <div className="w-full md:w-1/2 flex flex-col justify-center px-10 py-12 overflow-y-auto">
          <div className="max-w-xl">
            <h1 className="text-2xl font-bold text-gray-900 mb-8 leading-snug">
              Which Emirate do you want to form your business in?
            </h1>

            {/* Option cards */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div
                onClick={() => setSelected('sharjah')}
                className="p-4 rounded-xl cursor-pointer transition-all duration-200"
                style={{
                  border: selected === 'sharjah' ? '2px solid #F59E0B' : '2px solid #E5E7EB',
                  backgroundColor: selected === 'sharjah' ? '#FFFBEB' : 'white',
                }}
              >
                <p className="font-bold text-gray-900 text-base mb-1">{primaryEmirate}</p>
                <p className="text-sm text-gray-500 mb-3">{isMainland ? 'Only Emirate in Phase 1' : 'Recommended'}</p>
                <div className="flex justify-end">
                  <RadioCircle selected={selected === 'sharjah'} />
                </div>
              </div>

              <div
                onClick={() => setSelected('other')}
                className="p-4 rounded-xl cursor-pointer transition-all duration-200"
                style={{
                  border: selected === 'other' ? '2px solid #F59E0B' : '2px solid #E5E7EB',
                  backgroundColor: selected === 'other' ? '#FFFBEB' : 'white',
                }}
              >
                <p className="font-bold text-gray-900 text-base mb-1">Other</p>
                <p className="text-sm text-gray-500 mb-3">Choose other Emirate</p>
                <div className="flex justify-end">
                  <RadioCircle selected={selected === 'other'} />
                </div>
              </div>
            </div>

            {/* Fee table */}
            <div className="rounded-xl border border-gray-200 p-5 mb-5 bg-gray-50">
              <p className="font-bold text-gray-900 mb-4">Emirate fee</p>
              <div className="space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="font-semibold text-gray-800">Emirate</span>
                  <span className="text-gray-700">{displayEmirate}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold text-gray-800">Price</span>
                  <span className="text-gray-700">0</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold text-gray-800">Billing</span>
                  <span className="text-gray-700">One-time</span>
                </div>
              </div>
            </div>

            {/* Mainland Phase-1 note */}
            {isMainland && (
              <div className="rounded-xl border border-amber-200 p-5 mb-6 bg-amber-50">
                <p className="font-bold text-gray-900 mb-1.5">Dubai Mainland only (Phase 1)</p>
                <p className="text-sm text-gray-600">{MAINLAND_ONLY_MSG}</p>
              </div>
            )}

            {/* Sharjah recommendation (Free Zone) */}
            {selected === 'sharjah' && !isMainland && (
              <div className="rounded-xl border border-gray-200 p-5 mb-6 bg-gray-50">
                <p className="font-bold text-gray-900 mb-3">Why Sharjah is Recommended</p>
                <ul className="space-y-1.5">
                  {[
                    'Lower setup and licensing costs compared to Dubai/Abu Dhabi.',
                    'Popular Free Zones (Shams) are founder friendly.',
                    'Fast incorporation process (some in 2–3 days).',
                    'Ideal for e-commerce, digital services, and SMEs starting lean.',
                  ].map((item, i) => (
                    <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-gray-400 mt-0.5 shrink-0">·</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Other dropdown */}
            {selected === 'other' && (
              <div className="mb-6">
                <select
                  value={otherValue}
                  onChange={(e) => handleOther(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent text-base cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '20px',
                    paddingRight: '40px',
                    appearance: 'none',
                  }}
                >
                  {otherEmirates.map((e) => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-8 py-3 border-2 border-gray-300 rounded-lg font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="px-8 py-3 bg-amber-400 text-gray-900 font-semibold rounded-lg hover:bg-amber-500 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
